import {useEffect} from 'react'
import {createPortal} from 'react-dom'

interface ModalButton {
  text: string
  className?: string
  onClick: () => void
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  /** Optional test id for the footer button (e.g. destructive confirm). */
  dataTestId?: string
}

interface ModalProps {
  closeModal: () => void
  children?: JSX.Element
  title: string
  /** Optional Font Awesome class shown before the title. */
  titleIcon?: string
  buttons?: ModalButton[]
  /** Extra class on `.modal-dialog`, e.g. `modal-lg` to widen it. Default is 640px. */
  dialogClassName?: string
  // Keep these for backward compatibility
  submit?: () => void
  submitButtonText?: string
}

export const CustomModal = ({
  children,
  title,
  titleIcon,
  closeModal,
  buttons,
  dialogClassName,
  // Backward compatibility props
  submit,
  submitButtonText,
}: ModalProps) => {
  useEffect(() => {
    const keyDownHandler = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        event.stopPropagation()
        if (submit) {
          submit()
          closeModal()
        }
      } else if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        closeModal()
      }
    }
    document.addEventListener('keydown', keyDownHandler)
    return () => {
      document.removeEventListener('keydown', keyDownHandler)
    }
  }, [submit, closeModal])

  // Use new button configuration if provided, otherwise fall back to old behavior
  const modalButtons = buttons || [
    {
      text: 'Cancel',
      className: 'btn btn-light',
      onClick: closeModal,
    },
    {
      text: submitButtonText || 'Submit',
      className: 'btn btn-primary',
      onClick: () => {
        submit && submit()
        closeModal()
      },
    },
  ]

  // `.ob-dialog` (sass/components/_dialog.scss) supplies the shared header/body/footer chrome.
  const dialogClasses = ['modal-dialog', 'modal-dialog-centered', 'ob-dialog', dialogClassName]
    .filter(Boolean)
    .join(' ')

  // Portal to body so dialogs escape panel stacking contexts (e.g. .ob-files-panel z-index: 5)
  // that otherwise leave the modal under the fixed header.
  return createPortal(
    <>
      <div className={`modal-backdrop display-block show`}></div>
      <div className={`modal fade display-block show`}>
        <div className={dialogClasses} role='dialog' aria-modal='true'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>
                {titleIcon ? <i className={`${titleIcon} me-2`} aria-hidden='true' /> : null}
                {title}
              </h5>
            </div>
            <div className='modal-body'>{children}</div>
            <div className='modal-footer'>
              {modalButtons.map((button, index) => (
                <button
                  key={index}
                  type={button.type || 'button'}
                  className={button.className || 'btn btn-secondary'}
                  onClick={button.onClick}
                  disabled={button.disabled}
                  {...(button.dataTestId ? {'data-testid': button.dataTestId} : {})}
                >
                  {button.text}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  )
}
